import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, Input } from '@/components/ui/input';
import { ErrorState, PageHeader } from '@/components/ui/misc';

/**
 * Datos fiscales de la empresa: los que salen impresos en los pedidos y las facturas.
 *
 * Vivía como una pestaña dentro de Tesorería, que no es su sitio: esto es un dato de la empresa, no un
 * movimiento de dinero. Ahora cuelga de Administración y Tesorería se queda con lo suyo.
 */
interface Empresa {
  legalName: string; tradeName: string | null; vatNumber: string | null; address: string | null;
  city: string | null; province: string | null; postalCode: string | null; phone: string | null;
  email: string | null; website: string | null; currency: string;
  pdfConditions: string | null; hasLogo: boolean;
}

export function CompanyPage() {
  const { can } = useAuth();
  const puedeEditar = can('finance.write');
  // El logo se guarda por otra ruta y exige `settings.manage`, no `finance.write`: si no se mira, el
  // botón aparece y el servidor contesta 403.
  const puedeLogo = can('settings.manage');
  const qc = useQueryClient();

  const empresa = useQuery({
    queryKey: ['company'],
    queryFn: () => api<{ data: Empresa }>('/company')
  });

  /** Lo que está escribiendo el usuario; `null` = no hay cambios sin guardar. */
  const [cambios, setCambios] = useState<Empresa | null>(null);
  const [guardando, setGuardando] = useState(false);
  /**
   * La imagen del logo se sirve con la misma dirección siempre y con caché de 5 minutos: sin esto, tras
   * subir otro logo se seguiría viendo el anterior. Se rompe la caché con un contador propio y con la
   * hora de la última consulta.
   */
  const [versionLogo, setVersionLogo] = useState(0);

  const actual = cambios ?? empresa.data?.data ?? null;
  // `hasLogo` se lee del servidor: la copia local puede tener el valor anterior a subir o quitar el logo.
  const hasLogo = empresa.data?.data.hasLogo ?? false;

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['company'] });
    void qc.invalidateQueries({ queryKey: ['treasury'] });
  };

  const subirLogo = async (fichero?: File) => {
    if (!fichero) return;
    if (fichero.size > 1024 * 1024) { toast.error('El logo no puede pasar de 1 MB'); return; }
    try {
      // Se manda el fichero tal cual: la API mira la firma, no la extensión.
      await api('/company/logo', { method: 'PUT', raw: fichero });
      toast.success('Logo actualizado');
      setVersionLogo((v) => v + 1);
      refrescar();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo subir el logo');
    }
  };

  const borrarLogo = async () => {
    try {
      await api('/company/logo', { method: 'DELETE' });
      toast.success('Logo quitado');
      setVersionLogo((v) => v + 1);
      refrescar();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo quitar el logo');
    }
  };

  const guardar = async () => {
    if (!actual) return;
    setGuardando(true);
    try {
      await api('/company', {
        method: 'PUT',
        body: {
          legalName: actual.legalName,
          tradeName: actual.tradeName || null,
          vatNumber: actual.vatNumber || null,
          address: actual.address || null,
          city: actual.city || null,
          province: actual.province || null,
          postalCode: actual.postalCode || null,
          phone: actual.phone || null,
          email: actual.email || null,
          website: actual.website || null,
          currency: actual.currency,
          pdfConditions: actual.pdfConditions || null
        }
      });
      toast.success('Datos de la empresa guardados');
      setCambios(null);
      refrescar();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudieron guardar los datos');
    } finally { setGuardando(false); }
  };

  if (empresa.isError) {
    return <ErrorState message="No se pudieron cargar los datos de la empresa" onRetry={() => void empresa.refetch()} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Datos de la empresa"
        description="Razón social, CIF, dirección y logo que salen impresos en los pedidos y las facturas."
      />

      {actual ? (
        <Card className="space-y-3 p-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Razón social" required><Input value={actual.legalName} disabled={!puedeEditar} onChange={(e) => setCambios({ ...actual, legalName: e.target.value })} /></Field>
            <Field label="Nombre comercial"><Input value={actual.tradeName ?? ''} disabled={!puedeEditar} onChange={(e) => setCambios({ ...actual, tradeName: e.target.value })} /></Field>
            <Field label="CIF / VAT"><Input value={actual.vatNumber ?? ''} disabled={!puedeEditar} onChange={(e) => setCambios({ ...actual, vatNumber: e.target.value })} /></Field>
            <Field label="Dirección" className="sm:col-span-2"><Input value={actual.address ?? ''} disabled={!puedeEditar} onChange={(e) => setCambios({ ...actual, address: e.target.value })} /></Field>
            <Field label="Código postal"><Input value={actual.postalCode ?? ''} disabled={!puedeEditar} onChange={(e) => setCambios({ ...actual, postalCode: e.target.value })} /></Field>
            <Field label="Ciudad"><Input value={actual.city ?? ''} disabled={!puedeEditar} onChange={(e) => setCambios({ ...actual, city: e.target.value })} /></Field>
            <Field label="Provincia"><Input value={actual.province ?? ''} disabled={!puedeEditar} onChange={(e) => setCambios({ ...actual, province: e.target.value })} /></Field>
            <Field label="Teléfono"><Input value={actual.phone ?? ''} disabled={!puedeEditar} onChange={(e) => setCambios({ ...actual, phone: e.target.value })} /></Field>
            <Field label="Email"><Input value={actual.email ?? ''} disabled={!puedeEditar} onChange={(e) => setCambios({ ...actual, email: e.target.value })} /></Field>
            <Field label="Web"><Input value={actual.website ?? ''} disabled={!puedeEditar} onChange={(e) => setCambios({ ...actual, website: e.target.value })} /></Field>
          </div>
          <Field label="Condiciones impresas en los pedidos"
            hint="Validez de la oferta, forma y plazo de pago. Si se deja vacío, el PDF no imprime el bloque">
            <textarea
              className="min-h-20 w-full rounded-md border bg-background px-3 py-2 text-[12px]"
              disabled={!puedeEditar}
              value={actual.pdfConditions ?? ''}
              onChange={(e) => setCambios({ ...actual, pdfConditions: e.target.value })}
              placeholder="Precios en euros, IVA no incluido. Validez de la oferta: 30 días…"
            />
          </Field>
          <div className="flex items-center gap-3">
            {hasLogo ? (
              <img src={`/api/company/logo?v=${versionLogo}-${empresa.dataUpdatedAt}`} alt="Logo de la empresa" className="h-12 w-auto rounded border bg-white p-1" />
            ) : (
              <span className="text-[11px] text-muted-foreground">Sin logo: los PDF salen con el nombre de la empresa</span>
            )}
            {puedeLogo ? (
              <>
                <label className="cursor-pointer rounded-md border px-3 py-1.5 text-[12px] hover:bg-muted">
                  Subir logo (PNG o JPEG, máx. 1 MB)
                  <input type="file" accept="image/png,image/jpeg" className="hidden"
                    onChange={(e) => void subirLogo(e.target.files?.[0])} />
                </label>
                {hasLogo ? (
                  <Button variant="ghost" onClick={() => void borrarLogo()}>Quitar logo</Button>
                ) : null}
              </>
            ) : null}
          </div>
          {puedeEditar ? (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" disabled={!cambios} onClick={() => setCambios(null)}>Descartar</Button>
              <Button disabled={guardando || !cambios} onClick={() => void guardar()}>Guardar</Button>
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
